import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import FiltersPanel from "./filters-panel/filters-panel.jsx";
import { subDays, startOfMonth, subMonths, endOfMonth } from "date-fns";
import "chartjs-adapter-moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
} from "chart.js";
import { zonedTimeToUtc } from "date-fns-tz";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TimeScale
);

import AnalyticsChart from "./account-charts/analytics-chart.jsx";
import { getDefaultChartData } from "./account-charts/utils.js";
import api from "./api/facadeService.js";

import "../../styles/analytics/wrapper.scss";
import "../../styles/analytics/charts.scss";
import { linkedAccountUpdates$ } from "../../utils/subjects.js";
import { supportsAccountAnalytics } from "../../utils/analytics-utils.js";
import InfoBanner from "../common/info-banner.jsx";

const AccountAnalyticsPage = (props) => {
  const [accountCheckList, setAccountCheckList] = useState({});
  const [currentPeriodRange, setCurrentPeriodRange] = useState(null);
  const [comparisonPeriodRange, setComparisonPeriodRange] = useState(null);
  const [currentPeriodData, setCurrentPeriodData] = useState([]);
  const [comparisonPeriodData, setComparisonPeriodData] = useState([]);
  const [currentPeriodLoading, setCurrentPeriodLoading] = useState(true);
  const [comparisonPeriodLoading, setComparisonPeriodLoading] = useState(
    !props.isReportView
  );
  const [currentPeriod, setCurrentPeriod] = useState("currentMouthToDate");
  const [comparePeriod, setComparePeriod] = useState("previousMouthToDate");

  useEffect(() => {
    if (props.isReportView) return;
    props.agencyId &&
      loadData(currentPeriodRange, "currentPeriodData", {
        agencyId: props.agencyId,
      });
  }, [currentPeriodRange, props.agencyId]);

  useEffect(() => {
    if (props.isReportView) return;
    props.agencyId &&
      loadData(comparisonPeriodRange, "comparisonPeriodData", {
        agencyId: props.agencyId,
      });
  }, [comparisonPeriodRange, props.agencyId]);

  useEffect(() => {
    if (props.clientReport?.id) {
      initReportData();
    }
  }, [props.clientReport?.id]);

  const initReportData = () => {
    if (props.clientReport.currentAggregationAnalytics != null) {
      let currentPeriod = {
        startDate: new Date(props.clientReport.startDateString),
        endDate: new Date(props.clientReport.endDateString),
      };

      setCurrentPeriodRange((cpr) => ({
        ...cpr,
        ...currentPeriod,
      }));

      setCurrentPeriodData((cpd) => [
        ...cpd,
        ...props.clientReport.currentAggregationAnalytics,
      ]);
    }

    if (props.clientReport.compareAggregationAnalytics != null) {
      let comparePeriod = {
        startDate: new Date(props.clientReport.compareStartDateString),
        endDate: new Date(props.clientReport.compareEndDateString),
      };
      setComparisonPeriodRange((cpr) => ({
        ...cpr,
        ...comparePeriod,
      }));

      setComparisonPeriodData((cpd) => [
        ...cpd,
        ...props.clientReport.compareAggregationAnalytics,
      ]);
    } else {
      setComparePeriod("none");
    }

    setCurrentPeriodLoading(false);
    setComparisonPeriodLoading(false);
  };

  const loadData = async (
    dateRange,
    dateKey,
    { workspaceId, agencyId } = {}
  ) => {
    if (dateRange?.startDate && dateRange?.endDate) {
      const convertedRange = { ...dateRange };
      if (props.timezone) {
        convertedRange.startDate = zonedTimeToUtc(
          dateRange?.startDate,
          props.timezone
        );
        convertedRange.endDate = zonedTimeToUtc(
          dateRange?.endDate,
          props.timezone
        );
      }
      dateKey === "currentPeriodData"
        ? setCurrentPeriodLoading(true)
        : setComparisonPeriodLoading(true);
      try {
        const res = await api.getAggregateData(
          {
            workspaceId: workspaceId || props.workspaceId,
            startDate: convertedRange.startDate.toISOString(),
            endDate: convertedRange.endDate.toISOString(),
            granularity: "DAY",
          },
          agencyId
        );
        if (dateKey === "currentPeriodData") {
          setCurrentPeriodData(res.data.aggregateAnalytics);
        } else {
          setComparisonPeriodData(res.data.aggregateAnalytics);
        }
      } catch (error) {
        console.log("error ", error);
      } finally {
        dateKey === "currentPeriodData"
          ? setCurrentPeriodLoading(false)
          : setComparisonPeriodLoading(false);
      }
    }
  };

  const createAndSetAccountList = (accountList) => {
    let analyticsAccountList = accountList.filter((account) =>
      supportsAccountAnalytics(account.platformType)
    );
    const groupByCategory = Object.groupBy(analyticsAccountList, (account) => {
      return account.platformType;
    });

    setAccountCheckList(
      Object.keys(groupByCategory).reduce((destination, key) => {
        destination[key.toLowerCase()] = groupByCategory[key].map((acc) => ({
          ...acc,
          checked:
            props.isReportView && props.selectedAccountId
              ? props.selectedAccountId === acc.accountId
              : true,
        }));
        return destination;
      }, {})
    );
  };

  useEffect(() => {
    if (!props.isReportView) {
      const currentDate = new Date();

      if (currentDate.getDate() > 2) {
        setCurrentPeriodRange({
          startDate: startOfMonth(currentDate),
          endDate: subDays(currentDate.setHours(23, 59, 59, 999), 1),
          key: "selection",
        });

        setComparisonPeriodRange({
          startDate: startOfMonth(subMonths(currentDate, 1)),
          endDate: subMonths(
            subDays(currentDate.setHours(23, 59, 59, 999), 1),
            1
          ),
          key: "selection",
        });
      } else {
        setCurrentPeriodRange({
          startDate: startOfMonth(subMonths(currentDate, 1)),
          endDate: endOfMonth(subMonths(currentDate, 1)),
          key: "selection",
        });

        setComparisonPeriodRange({
          startDate: startOfMonth(subMonths(currentDate, 2)),
          endDate: endOfMonth(subMonths(currentDate, 2)),
          key: "selection",
        });
        setCurrentPeriod("lastMonth");
        setComparePeriod("previousMouth");
      }
    }
  }, []);

  useEffect(() => {
    createAndSetAccountList(props.accountList);
  }, [props.accountList?.length, props.selectedAccountId]);

  useEffect(() => {
    linkedAccountUpdates$.subscribe({
      next: (data) => {
        if (
          data.action === "ACCOUNT_ADDED" ||
          data.action === "ACCOUNT_REMOVED"
        ) {
          createAndSetAccountList(data.accounts);
        }
      },
    });
  }, []);

  const disableCompareData = comparePeriod === "none";

  const checkIfChartShouldBeShownToClient = (chartParam) => {
    /* If component is embedded in a report, check the report parameters 
    to see if the chart should be shown */
    return !props.isReportView ? true : chartParam;
  };

  return (
    <Box
      className={props.isReportView ? "in-report-caontainer" : "page-container"}
      style={{ marginLeft: props.isPdfView ? "14px" : "40px" }}
    >
      <Box style={{ width: "100%", paddingLeft: "0px" }}>
        {!props.isReportView && (
          <Box sx={{ marginBlock: "24px" }}>
            <FiltersPanel
              accountCheckList={accountCheckList}
              setAccountCheckList={setAccountCheckList}
              accountAnalytics={props.accountAnalytics}
              currentPeriodRange={currentPeriodRange}
              comparisonPeriodRange={comparisonPeriodRange}
              setCurrentPeriodRange={setCurrentPeriodRange}
              setComparisonPeriodRange={setComparisonPeriodRange}
              currentPeriod={currentPeriod}
              comparePeriod={comparePeriod}
              setCurrentPeriod={setCurrentPeriod}
              setComparePeriod={setComparePeriod}
            />
          </Box>
        )}

        {!props.isReportView && (
          <>
            <InfoBanner
              text={"Linking Facebook Groups has been deprecated by Meta"}
              subtext={
                "As of April 15th, 2024, publishing Content directly to Facebook Groups and collecting Analytics metrics for them are no longer available. Historical data for existing linked Facebook Groups will be retained. Meta's decision to deprecate the Facebook Groups API affects all 3rd party platforms. You may use our push notification system for Facebook Groups moving forward by adding an email address to the account, or"
              }
              link={"https://www.facebook.com/business/help/382096348876188"}
              linkText={
                " follow Meta's recommendation to migrate Groups to Pages."
              }
              featureFlags={props.featureFlags}
              style={"warning"}
              shouldDisplayBanner={props.shouldDisplayFacebookBanner}
            ></InfoBanner>
            <InfoBanner
              text={"Re-Link Your LinkedIn Accounts"}
              subtext={"Due to changes to the LinkedIn API, you will need to "}
              link={"/dashboard?view=brand_settings"}
              linkText={"re-link your LinkedIn accounts"}
              postLinkText={
                " in order to restore data collection on Analytics and Activity Feed. Publishing to LinkedIn accounts is not affected by this change."
              }
              style={"info"}
              shouldDisplayBanner={props.shouldDisplayOldLinkedInCredsBanner}
            ></InfoBanner>
          </>
        )}

        {checkIfChartShouldBeShownToClient(
          props.clientReport?.showOverviewFollowerData
        ) && (
          <AnalyticsChart
            title="Audience Growth"
            description="Refers to the increase in followers or subscribers on a content platform. Key metrics include Total Followers, Followers Gained, and Followers Lost. These metrics help assess how well a platform is attracting and retaining its audience."
            metricFields={[
              {
                key: "followers",
                label: "Total Followers",
                tooltip:
                  "The total number of people that follow your brand's page(s) on social media",
                negativeMetric: false,
                aggregateType: "lastDay",
              },
              {
                key: "followersGained",
                label: "Followers Gained",
                tooltip:
                  "The number of people that started following your brand’s page(s) during the current reporting period. Note that daily Followers Gained will not necessarily tie back to the Total Followers due to platform limitations. This metric is not available for Facebook.",
                negativeMetric: false,
                aggregateType: "sum",
              },
              {
                key: "followersLost",
                label: "Followers Lost",
                tooltip:
                  "The number of people that stopped following your brand’s page(s) during the current reporting period. Note that daily Followers Lost will not necessarily tie back to the Total Followers due to platform limitations. This metric is not available for Facebook.",
                negativeMetric: true,
                aggregateType: "sum",
              },
              !!accountCheckList?.facebook?.filter((x) => x.checked)?.length
                ? {
                    key: "facebookFans",
                    label: "Facebook Fans",
                    tooltip:
                      "The total number of people that have liked your brand's Facebook page(s). Deprecated by Meta as of 11/15/2025.",
                    negativeMetric: false,
                    aggregateType: "lastDay",
                  }
                : null,
            ].filter((x) => x)}
            metricTable={{
              title: "Audience Growth Metrics",
              rows: {
                followers: "Followers",
                followersGained: "Followers Gained",
                followersLost: "Followers Lost",
                facebookFans: "Fans",
              },
              headers: {
                followers: {
                  title: "Total Followers",
                  tooltip:
                    "The amount of followers you have as of the final day of the selected reporting period",
                },
                followersGained: {
                  title: "Total Followers Gained",
                  tooltip:
                    "The total amount of followers you gained during the selected reporting period. Note that daily Followers Gained or Lost will not necessarily tie back to Total Followers due to platform limitations.",
                },
                followersLost: {
                  title: "Total Followers Lost",
                  tooltip:
                    "The total amount of followers you lost during the selected reporting period. Note that daily Followers Gained or Lost will not necessarily tie back to Total Followers due to platform limitations.",
                },
                facebookFans: {
                  title: "Facebook Fans",
                  tooltip:
                    "The total number of people that have liked your brand's Facebook page(s). Deprecated by Meta as of 11/15/2025.",
                },
              },
              includeFun: (selectedMetric) => selectedMetric !== "facebookFans",
            }}
            datasetOptions={{ fill: true }}
            defaultChartData={getDefaultChartData(
              ["followers", "followersGained", "followersLost", "facebookFans"],
              accountCheckList
            )}
            aggregationAnalyticsList={{
              currentPeriodData,
              comparisonPeriodData,
            }}
            accountCheckList={accountCheckList}
            showMetricTable={!props.selectedAccountId}
            showChartLegend={!props.selectedAccountId}
            currentPeriodRange={currentPeriodRange}
            comparisonPeriodRange={comparisonPeriodRange}
            isLoading={currentPeriodLoading || comparisonPeriodLoading}
            disableCompareData={disableCompareData}
            aggregateType="lastDay"
            stacked
            excludeMetrics={[
              {
                key: "facebook",
                metrics: ["followersGained", "followersLost"],
              },
            ]}
          />
        )}

        {checkIfChartShouldBeShownToClient(
          props.clientReport?.showOverviewExposureData
        ) && (
          <AnalyticsChart
            title="Exposure"
            description="These statistics assess the extent and impact of your content's
          visibility. Exposure metrics may include impressions, which denote the
          total number of times your content was viewed, and reach, indicating
          the distinct number of users who have viewed your content. These
          metrics offer insights into the distribution of your content and its
          effectiveness in reaching your target audience. Threads does not currently expose these metrics at the account level."
            metricFields={[
              {
                key: "impressions",
                label: "Impressions",
                tooltip:
                  "The number of times your content is viewed by someone.",
                negativeMetric: false,
                aggregateType: "sum",
              },
              {
                key: "reach",
                label: "Reach",
                tooltip:
                  "The number of distinct individuals who saw the content at least once. Note that daily reach numbers will not add up to overall reach for a given period due to overlapping audiences from day to day.",
                negativeMetric: false,
                aggregateType: "sum",
              },
            ]}
            metricTable={{
              title: "Exposure Metrics",
              rows: {
                impressions: "Impressions",
                reach: "Reach",
              },
              headers: {
                impressions: {
                  title: "Total Impressions",
                  tooltip:
                    "The amount of times your content was viewed during the selected reporting period",
                },
                reach: {
                  title: "Total Reach",
                  tooltip:
                    "The amount of unique users that saw your content during the selected reporting period",
                },
              },
            }}
            defaultChartData={getDefaultChartData(
              ["impressions", "reach"],
              accountCheckList
            )}
            aggregationAnalyticsList={{
              currentPeriodData,
              comparisonPeriodData,
            }}
            accountCheckList={accountCheckList}
            showMetricTable={!props.selectedAccountId}
            showChartLegend={!props.selectedAccountId}
            currentPeriodRange={currentPeriodRange}
            comparisonPeriodRange={comparisonPeriodRange}
            isLoading={currentPeriodLoading || comparisonPeriodLoading}
            disableCompareData={disableCompareData}
          />
        )}

        {checkIfChartShouldBeShownToClient(
          props.clientReport?.showOverviewEngagementBreakdown
        ) && (
          <AnalyticsChart
            title="Engagement Breakdown"
            description="Engagement Breakdown refers to the analysis of various metrics that
          measure the level of interaction and engagement that users have with
          your content. This includes tracking the number of reactions, comments,
          shares, and clicks your content receives. By monitoring the engagement
          breakdown, you can gain insight into what type of content resonates
          most with your audience, allowing for more informed decision-making
          and strategy optimization. Meta has deprecated Instagram click metrics as of January 8th, 2025, 
          which will impact Engagement Rate calculations from that date forward for Instagram. Note that Facebook does not provide individual engagement metrics 
          for comments, shares, or clicks. Engagements outside the selected time period are excluded, even 
          if the post that was interacted with was originally published during the selected time period. Threads does not currently expose click metrics at the account level."
            metricFields={[
              {
                key: "overall",
                label: "Overall",
                tooltip:
                  "The cumulative amount of reactions, comments, shares and clicks garnered during the selected reporting period",
                negativeMetric: false,
                aggregateType: "sum",
              },
              {
                key: "reactions",
                label: "Reactions",
                tooltip:
                  "The total number of reactions your content has received during the selected reporting period",
                negativeMetric: false,
                aggregateType: "sum",
              },
              {
                key: "comments",
                label: "Comments",
                tooltip:
                  "The total number of comments left on your content during the selected reporting period",
                negativeMetric: false,
                aggregateType: "sum",
              },
              {
                key: "shares",
                label: "Shares",
                tooltip:
                  "The total number of times your content has been shared during the selected reporting period",
                negativeMetric: false,
                aggregateType: "sum",
              },
              {
                key: "clicks",
                label: "Clicks",
                tooltip:
                  "The total number of clicks on your content or page during the selected reporting period. Note that Meta has deprecated Instagram click metrics as of January 8th, 2025.",
                negativeMetric: false,
                aggregateType: "sum",
              },
            ]}
            metricTable={{
              title: "Engagement Metrics",
              rows: {
                overall: "Engagement",
                reactions: "Reactions",
                comments: "Comments",
                shares: "Shares",
                clicks: "Clicks",
              },
              headers: {
                overall: {
                  title: "Total Engagement",
                  tooltip:
                    "The cumulative amount of reactions (likes), comments, shares and clicks garnered during the selected reporting period",
                },
                reactions: {
                  title: "Total Reactions",
                  tooltip:
                    "The total number of reactions (likes and other interactions) your content has received during the selected reporting period",
                },
                comments: {
                  title: "Total Comments",
                  tooltip:
                    "The total number of comments left on your content during the selected reporting period",
                },
                shares: {
                  title: "Total Shares",
                  tooltip:
                    "The total number of times your content has been shared during the selected reporting period",
                },
                clicks: {
                  title: "Total Clicks",
                  tooltip:
                    "The total number of clicks on your content or page during the selected reporting period",
                },
              },
            }}
            datasetOptions={{ fill: true }}
            defaultChartData={getDefaultChartData(
              ["overall", "reactions", "comments", "shares", "clicks"],
              accountCheckList
            )}
            aggregationAnalyticsList={{
              currentPeriodData: currentPeriodData.map((data) => ({
                ...data,
                overall:
                  data.reactions + data.comments + data.shares + data.clicks,
              })),
              comparisonPeriodData: comparisonPeriodData.map((data) => ({
                ...data,
                overall:
                  data.reactions + data.comments + data.shares + data.clicks,
              })),
            }}
            accountCheckList={accountCheckList}
            showMetricTable={!props.selectedAccountId}
            showChartLegend={!props.selectedAccountId}
            currentPeriodRange={currentPeriodRange}
            comparisonPeriodRange={comparisonPeriodRange}
            isLoading={currentPeriodLoading || comparisonPeriodLoading}
            disableCompareData={disableCompareData}
            stacked
          />
        )}

        {checkIfChartShouldBeShownToClient(
          props.clientReport?.showOverviewEngagementRate
        ) && (
          <AnalyticsChart
            title="Engagement Rate"
            description="This is a key performance indicator (KPI) often used to gauge the effectiveness of social media content. It is calculated
             by dividing the total engagement (reactions, comments, shares, etc.) by the impressions on a given day. The Engagement Rate for a given 
             platform expressed in the table below will be equal to the figure at the end of the selected period. Note that certain platforms may 
             calculate engagement using a different metric than impressions, and therefore may not match our calculations. Threads does not currently expose engagement rate metrics at the account level."
            metricFields={[
              {
                key: "engagement",
                label: "Engagement",
                tooltip:
                  "The number of times your content is viewed by someone.",
                negativeMetric: false,
              },
            ]}
            metricTable={{
              title: "Engagement Rate Metrics",
              rows: {
                engagement: "Engagement Rate",
              },
              headers: {
                engagement: {
                  title: "Average Engagement Rate",
                  tooltip:
                    "The rate of engagement (reactions, comments, shares, clicks) relative to the number of views on your content. Engagement Rate = Engagment / Impressions",
                },
              },
            }}
            defaultChartData={getDefaultChartData(
              ["engagement"],
              accountCheckList
            )}
            aggregationAnalyticsList={{
              currentPeriodData,
              comparisonPeriodData,
            }}
            accountCheckList={accountCheckList}
            showMetricTable={!props.selectedAccountId}
            showChartLegend={!props.selectedAccountId}
            currentPeriodRange={currentPeriodRange}
            comparisonPeriodRange={comparisonPeriodRange}
            isLoading={currentPeriodLoading || comparisonPeriodLoading}
            totalType="avarage"
            disableCompareData={disableCompareData}
            percentMetric={true}
          />
        )}
      </Box>
    </Box>
  );
};

export default AccountAnalyticsPage;
